/**
 * Details Modal Component
 * Displays validation result details in a user-friendly modal format
 */

import React from 'react';
import { X, AlertCircle, CheckCircle, Info } from 'lucide-react';

interface DetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  details: Record<string, any>;
  title?: string;
}

export const DetailsModal: React.FC<DetailsModalProps> = ({
  isOpen,
  onClose,
  details,
  title = 'Validation Details',
}) => {
  if (!isOpen) return null;

  // Fields to exclude from display (too technical or redundant)
  const excludedFields = new Set([
    'entities_used',
    'entitiesUsed',
    'dbIds',
    'elementIds',
    'internal_id',
    'raw_data',
  ]);

  // User-friendly field name mappings
  const fieldLabels: Record<string, string> = {
    measured: 'Measured Value',
    required: 'Required Value',
    actual: 'Actual Value',
    expected: 'Expected Value',
    location: 'Location',
    distance: 'Distance',
    clearance: 'Clearance',
    width: 'Width',
    height: 'Height',
    depth: 'Depth',
    angle: 'Angle',
    count: 'Count',
    status: 'Status',
    passed: 'Passed',
    failed: 'Failed',
    warning: 'Warning',
    notes: 'Notes',
    recommendation: 'Recommendation',
    reason: 'Reason',
    description: 'Description',
    code_reference: 'Code Reference',
    standard: 'Standard',
    section: 'Section',
  };

  // Format the details object into readable key-value pairs
  const formatValue = (value: any): React.ReactNode => {
    if (value === null || value === undefined) {
      return <span className="text-gray-400 italic">Not specified</span>;
    }

    if (typeof value === 'boolean') {
      return value ? (
        <span className="inline-flex items-center gap-1 text-green-700">
          <CheckCircle size={14} />
          Yes
        </span>
      ) : (
        <span className="inline-flex items-center gap-1 text-red-700">
          <AlertCircle size={14} />
          No
        </span>
      );
    }

    if (typeof value === 'number') {
      return <span className="font-medium">{value.toLocaleString()}</span>;
    }

    if (typeof value === 'string') {
      // Check if it's a long text (description, notes, etc.)
      if (value.length > 100) {
        return (
          <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
            {value}
          </div>
        );
      }
      return <span className="font-medium">{value}</span>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-400 italic">None</span>;
      }

      // Check if array contains objects
      if (typeof value[0] === 'object' && value[0] !== null) {
        // Don't show array of objects (like entities_used)
        return <span className="text-gray-400 italic">{value.length} items</span>;
      }

      // Simple array - show as list
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, idx) => (
            <li key={idx} className="text-sm">{String(item)}</li>
          ))}
        </ul>
      );
    }

    if (typeof value === 'object') {
      // Check for location object
      if ('x' in value && 'y' in value && 'z' in value) {
        return (
          <span className="font-mono text-sm">
            ({value.x.toFixed(2)}, {value.y.toFixed(2)}, {value.z.toFixed(2)})
          </span>
        );
      }

      // For other objects, show nested key-values
      return (
        <div className="pl-4 border-l-2 border-gray-200 space-y-2 mt-2">
          {Object.entries(value).map(([k, v]) => (
            <div key={k} className="text-sm">
              <span className="text-gray-600">{formatKey(k)}:</span>{' '}
              <span className="text-gray-900">{formatValue(v)}</span>
            </div>
          ))}
        </div>
      );
    }

    return <span>{String(value)}</span>;
  };

  // Format key names to be more readable
  const formatKey = (key: string): string => {
    // Use custom label if available
    if (fieldLabels[key.toLowerCase()]) {
      return fieldLabels[key.toLowerCase()];
    }

    // Convert snake_case or camelCase to Title Case
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/\b\w/g, (char) => char.toUpperCase())
      .trim();
  };

  // Filter and sort the details
  const filteredDetails = Object.entries(details)
    .filter(([key]) => !excludedFields.has(key))
    .sort(([keyA], [keyB]) => {
      // Prioritize certain fields at the top
      const priority = ['status', 'notes', 'measured', 'required', 'actual', 'expected'];
      const priorityA = priority.indexOf(keyA.toLowerCase());
      const priorityB = priority.indexOf(keyB.toLowerCase());

      if (priorityA !== -1 && priorityB !== -1) return priorityA - priorityB;
      if (priorityA !== -1) return -1;
      if (priorityB !== -1) return 1;

      return keyA.localeCompare(keyB);
    });

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden pointer-events-auto animate-in fade-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center gap-2">
              <Info size={20} className="text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-white/50"
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-5 overflow-y-auto max-h-[calc(85vh-10rem)] bg-gray-50">
            {filteredDetails.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Info size={32} className="mx-auto mb-2 opacity-50" />
                <p>No additional details available</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredDetails.map(([key, value]) => (
                  <div
                    key={key}
                    className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-sm transition-shadow"
                  >
                    <dt className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500"></span>
                      {formatKey(key)}
                    </dt>
                    <dd className="text-sm text-gray-900 ml-3.5">
                      {formatValue(value)}
                    </dd>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-white">
            <button
              onClick={onClose}
              className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium shadow-sm hover:shadow"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
